import { reportRecoverableError } from '../core/diagnostics'

export interface CopyResult {
  ok: boolean
  method: 'clipboard-api' | 'exec-command' | 'none'
}

/**
 * Copies text to the clipboard. Handles Persian/BiDi text safely (no
 * mutation of the string) and falls back to execCommand for older
 * browsers / insecure contexts.
 */
export async function copy(text: string): Promise<CopyResult> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text)
      return { ok: true, method: 'clipboard-api' }
    }
    catch (error: unknown) {
      reportRecoverableError('Clipboard API failed; using the legacy fallback', error)
      // fall through to legacy method
    }
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.top = '-1000px'
  textarea.style.left = '-1000px'
  document.body.appendChild(textarea)
  textarea.select()
  textarea.setSelectionRange(0, textarea.value.length)

  let ok = false
  try {
    ok = document.execCommand('copy')
  }
  catch (error: unknown) {
    reportRecoverableError('legacy clipboard copy failed', error)
    ok = false
  }
  finally {
    document.body.removeChild(textarea)
  }

  return { ok, method: ok ? 'exec-command' : 'none' }
}
